import { formatUnits } from 'viem';
import { useMemo } from 'react';
import { useGasPrice } from '@/onchain/hooks/useGasPrice';
import { useTokenPrice } from '@/hooks/useTokenPrice';
import { format, DisplayType } from '@/utils/numberFormatter';

/**
 * Hook to calculate network cost in USD for a transaction using EIP-1559 gas pricing
 * that accounts for L2 execution and L1 data fee
 * @param gasLimit - The estimated gas limit
 * @param enabled - Optional flag to conditionally enable/disable the calculation
 * @returns Formatted network cost string (e.g. "$1.23" or "<$0.01")
 */
export function useNetworkCost(gasLimit: bigint, enabled = true): string | null {
  // Pass the enabled flag to child hooks to prevent unnecessary API calls
  const { priceData } = useGasPrice(enabled);
  const { price: ethPrice } = useTokenPrice('ETH', enabled);

  const networkCost = useMemo(() => {
    // Skip calculation if not enabled or if we don't have the required data
    if (!enabled || !priceData?.maxFeePerGas || !ethPrice) return null;

    // Calculate max gas cost in ETH using EIP-1559 pricing for L2 execution + L1 data fee
    const gasCostWei = gasLimit * priceData.maxFeePerGas + priceData.l1DataFee;
    const gasCostEth = Number(formatUnits(gasCostWei, 18));

    // Calculate USD cost
    const usdCost = gasCostEth * ethPrice;

    return format({ value: usdCost, type: DisplayType.FiatShort });
  }, [priceData, ethPrice, gasLimit, enabled]);

  return networkCost;
}
